import type { SessionInfo } from '@contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { advanceEpoch, getSession, onApiEvent, signOut as postSignOut } from '../lib/api';
import { getPendingSignIn } from '../lib/second-factor';
import { useToast } from '../ui/toast';
import { NoAccess } from './NoAccess';
import { PortalHome } from '../portal/PortalHome';
import { SecondFactor } from './SecondFactor';
import { BootError, SignedOut, Splash } from './SignedOut';

export type SignedOutReason = 'expired' | 'signed-out' | 'denied' | 'failed' | 'cancelled' | 'unavailable' | null;

interface SessionValue { session: SessionInfo; signOut: () => Promise<void>; signingOut: boolean; refresh: () => void }
const SessionContext = createContext<SessionValue | null>(null);
export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside a signed-in tree');
  return value;
}

const RETURN_KEY = 'rmm.returnTo';
/** The least time between session checks that refused requests ask for. */
const RECHECK_MS = 5_000;
const reasons = ['denied', 'failed', 'cancelled', 'unavailable'] as const;

/** The callback can only report a coarse reason; read it once and clean the URL. */
function takeSigninParam() {
  const url = new URL(window.location.href);
  const reason = url.searchParams.get('signin');
  if (reason === null) return null;
  url.searchParams.delete('signin');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return reason;
}
const reasonOf = (param: string | null): SignedOutReason =>
  param === null || param === 'second-factor' ? null : (reasons as readonly string[]).includes(param) ? param as SignedOutReason : 'failed';

/** Remember where a signed-out visitor was headed, so sign-in can return them there. */
export function rememberReturnPath() {
  const path = `${window.location.pathname}${window.location.search}`;
  try { if (path.startsWith('/w/')) sessionStorage.setItem(RETURN_KEY, path); } catch { /* storage unavailable */ }
}
export function takeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && path.startsWith('/w/') && !path.startsWith('//') ? path : null;
  } catch { return null; }
}

export function SessionGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [signinParam] = useState(takeSigninParam);
  const [reason, setReason] = useState<SignedOutReason>(() => reasonOf(signinParam));
  // Google is done and the second factor is next (WRMM-134): shown even over a session this browser still holds (signing
  // in again, or as someone else), which ends only once the second factor succeeds.
  const secondStep = signinParam === 'second-factor';
  const [signingOut, setSigningOut] = useState(false);
  const [startOver, setStartOver] = useState(false);
  // Enrolling sets the session cookie while its recovery codes are still on screen: a session refetch (window focus,
  // the timer) must not replace them with the console before they are saved.
  const [showingCodes, setShowingCodes] = useState(false);
  const hadSession = useRef(false);
  const query = useQuery({
    queryKey: ['session'], queryFn: ({ signal }) => getSession(signal),
    staleTime: 60_000, refetchInterval: 5 * 60_000, refetchOnWindowFocus: true,
  });

  // A 401 or 403 rechecks the session. If a membership is gone, its workspace
  // unmounts and drops its data; the refused view itself shows an error, not old
  // results. (Removing an active query here would only refetch it into a loop.)
  // A check already under way is left to finish rather than restarted, and checks come at most every RECHECK_MS: the
  // first at once, and any refusals meanwhile as one more check when that time is up (WRMM-240).
  useEffect(() => {
    let last = -Infinity;
    let pending: ReturnType<typeof setTimeout> | undefined;
    const recheck = () => {
      pending = undefined;
      last = Date.now();
      void queryClient.invalidateQueries({ queryKey: ['session'] }, { cancelRefetch: false });
    };
    const stop = onApiEvent(() => {
      if (pending) return;
      const wait = last + RECHECK_MS - Date.now();
      if (wait <= 0) recheck(); else pending = setTimeout(recheck, wait);
    });
    return () => { stop(); clearTimeout(pending); };
  }, [queryClient]);

  const session = query.data;
  useEffect(() => {
    if (session) { hadSession.current = true; setReason(null); return; }
    if (session === null && hadSession.current) {
      hadSession.current = false;
      advanceEpoch();
      queryClient.removeQueries({ queryKey: ['scope'] });
      setReason(r => (r === 'signed-out' ? r : 'expired'));
    }
  }, [session, queryClient]);

  const signOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await postSignOut();
      setReason('signed-out');
      hadSession.current = false;
      advanceEpoch();
      queryClient.clear();
      queryClient.setQueryData(['session'], null);
    } catch {
      toast({ title: 'Sign-out couldn’t be confirmed', body: 'Your session may still be active. Check your connection and try again.', tone: 'warning' });
    } finally { setSigningOut(false); }
  }, [queryClient, toast]);
  const refresh = useCallback(() => { void query.refetch(); }, [query]);
  // A completed second factor set the session cookie: load the session as for any sign-in (the scope picks up a
  // remembered return path). The cookie may be someone else's than before (a link opened in a browser that was signed
  // in), so nothing of the old one is shown meanwhile, or if loading fails.
  const signedIn = () => {
    advanceEpoch();
    queryClient.removeQueries({ predicate: q => q.queryKey[0] !== 'session' });
    void queryClient.resetQueries({ queryKey: ['session'] });
  };
  // Signed out, but perhaps halfway through a sign-in: Google done, the second factor still to come (WRMM-134).
  const pending = useQuery({ queryKey: ['pending-sign-in'], queryFn: ({ signal }) => getPendingSignIn(signal),
    enabled: (session === null || secondStep) && !startOver, staleTime: Infinity, retry: false });

  if (showingCodes && pending.data) {
    return <SecondFactor pending={pending.data} onCodes={setShowingCodes} onSignedIn={() => { setShowingCodes(false); signedIn(); }} onCancel={() => setStartOver(true)} />;
  }
  if (query.isPending || (secondStep && !startOver && pending.isPending)) return <Splash />;
  if (session === undefined) return <BootError onRetry={refresh} />;
  if (session === null && !startOver && pending.isPending) return <Splash />;
  if ((session === null || secondStep) && !startOver && pending.data) {
    return <SecondFactor pending={pending.data} onCodes={setShowingCodes} onSignedIn={signedIn} onCancel={() => setStartOver(true)} />;
  }
  if (session === null) {
    // A password passed: look again for the pending sign-in it made.
    const firstFactor = () => { setStartOver(false); void queryClient.invalidateQueries({ queryKey: ['pending-sign-in'] }); };
    return <SignedOut reason={reason} onFirstFactor={firstFactor} />;
  }
  const value = { session, signOut, signingOut, refresh };
  return (
    <SessionContext.Provider value={value}>
      {session.memberships.length ? children : session.customerUser ? <PortalHome /> : <NoAccess />}
    </SessionContext.Provider>
  );
}
